import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import toast from 'react-hot-toast'
import { 
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import Button from '../../components/common/Button'
import OnboardingLayout from '../../components/common/OnboardingLayout'
import { kycApi } from '../../services/api'
import { updateOnboardingStep, updateUser } from '../../store/slices/authSlice'
import { RootState } from '../../store/store'
import { buildKYCConfirmationPayload, normalizeKYCDate } from '../../utils/kycFlow'
import { validateAge18Plus } from '../../utils/validation'

const KYCPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const dispatch = useDispatch()
  const { user } = useSelector((s: RootState) => s.auth)
  const [kycStatus, setKycStatus] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [fetchingCams, setFetchingCams] = useState(false)
  const [camsDobRequired, setCamsDobRequired] = useState(false)
  const [camsDob, setCamsDob] = useState('')
  const [camsError, setCamsError] = useState('')
  const [camsSuccess, setCamsSuccess] = useState('')
  const next = searchParams.get('next') || '/dashboard/checkout'

  useEffect(() => {
    fetchKYCStatus()
  }, [])

  const fetchKYCStatus = async () => {
    try {
      setLoading(true)
      const status = await kycApi.getKYCStatus()
      setKycStatus(status)
    } catch (error) {
      console.error('Error fetching KYC status:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleFetchFromCams = async () => {
    setFetchingCams(true)
    setCamsError('')
    setCamsSuccess('')
    try {
      const formattedDob = camsDobRequired ? normalizeKYCDate(camsDob) || undefined : undefined
      const res = await kycApi.fetchFromCams(formattedDob)
      if (res.dob_required) {
        setCamsDobRequired(true)
        setCamsError('Please provide your Date of Birth to fetch from CAMS.')
      } else if (res.kyc_found && res.kyc_data) {
        const payload = buildKYCConfirmationPayload(res.kyc_data, user, true, formattedDob)
        const response = await kycApi.confirmKYC(payload)
        dispatch(updateUser({
          full_name: response.kyc.full_name || '',
          kyc_status: response.kyc_status,
          onboarding_step: response.onboarding_step,
        }))
        setCamsSuccess('KYC found successfully. Proceeding to agreement signing.')
        dispatch(updateOnboardingStep('esign'))
        toast.success('KYC found successfully. Proceeding to agreement signing.')
        setCamsDobRequired(false)
        navigate(`/dashboard/esign?next=${encodeURIComponent(next)}`)
      } else {
        const message = 'KYC not found. Our team will reach out to you shortly. You can continue with agreement signing and our team will follow up.'
        const payload = buildKYCConfirmationPayload(res.kyc_data, user, false, formattedDob)
        const response = await kycApi.confirmKYC(payload)
        dispatch(updateUser({
          full_name: response.kyc.full_name || '',
          kyc_status: response.kyc_status,
          onboarding_step: response.onboarding_step,
        }))
        setCamsSuccess(message)
        dispatch(updateOnboardingStep('esign'))
        toast(message)
        setCamsDobRequired(false)
        navigate(`/dashboard/esign?next=${encodeURIComponent(next)}`)
      }
    } catch (err: any) {
      const message = err.response?.data?.detail || 'Failed to fetch from CAMS.'
      setCamsError(message)
      toast.error(message)
    } finally {
      setFetchingCams(false)
    }
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'not_started': { 
        color: 'bg-dark-700 text-gray-400 border border-dark-600', 
        icon: ExclamationTriangleIcon, 
        text: 'Not Started' 
      },
      'pending': { 
        color: 'bg-yellow-100 text-yellow-800', 
        icon: ClockIcon, 
        text: 'Under Review' 
      },
      'under_review': {
        color: 'bg-yellow-100 text-yellow-800',
        icon: ClockIcon,
        text: 'Under Review'
      },
      'approved': { 
        color: 'status-completed', 
        icon: CheckCircleIcon, 
        text: 'Approved' 
      },
      'rejected': { 
        color: 'bg-red-100 text-red-800', 
        icon: XCircleIcon, 
        text: 'Rejected' 
      }
    }

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.not_started
    const IconComponent = config.icon

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        <IconComponent className="w-4 h-4 mr-1" />
        {config.text}
      </span>
    )
  }

  if (loading) {
    return (
      <OnboardingLayout currentStep="account" showSteps={false} title="KYC Verification" subtitle="Required before plan purchase.">
        <div className="flex items-center justify-center min-h-96">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </OnboardingLayout>
    )
  }

  // If KYC is approved, show read-only view
  if (kycStatus && kycStatus.status === 'approved') {
    return (
      <OnboardingLayout
        currentStep="account"
        showSteps={false}
        title="KYC Verification"
        subtitle="KYC is completed for purchase compliance."
      >
        <div className="text-center py-2">
          <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-gray-100">KYC Approved</h3>
          <p className="mt-2 text-gray-400">Your KYC has been successfully verified</p>
          {getStatusBadge(kycStatus.status)}
        </div>

        <div className="mt-6 rounded-xl border border-dark-600 bg-dark-900/60 p-4 sm:p-6">
          <h3 className="text-lg font-semibold text-gray-100 mb-4">Your KYC Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-500">Full Name</label>
              <p className="mt-1 text-sm text-gray-100">{kycStatus.full_name}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">PAN Number</label>
              <p className="mt-1 text-sm text-gray-100 font-mono">{kycStatus.pan_number}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Date of Birth</label>
              <p className="mt-1 text-sm text-gray-100">
                {new Date(kycStatus.date_of_birth).toLocaleDateString('en-IN')}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Gender</label>
              <p className="mt-1 text-sm text-gray-100">{kycStatus.gender}</p>
            </div>
          </div>
        </div>
      </OnboardingLayout>
    )
  }

  return (
    <OnboardingLayout
      currentStep="account"
      showSteps={false}
      title="KYC Verification"
      subtitle="Complete KYC to continue to agreement signing and payment."
    >
      <div className="space-y-4">
        {kycStatus && getStatusBadge(kycStatus.status)}

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
          <div>
            <h3 className="text-xl font-semibold text-gray-100">KYC Verification</h3>
            <p className="mt-1 text-sm text-gray-400">
              We’ll check your KYC record and update you with the result.
            </p>
          </div>
          <div className="flex flex-col items-stretch sm:items-end w-full sm:w-auto">
            {camsDobRequired && (
              <>
                <input
                  type="date"
                  value={camsDob}
                  onChange={e => setCamsDob(e.target.value)}
                  max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                  className="mb-2 w-full bg-dark-800 border border-dark-600 text-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="DOB for CAMS"
                />
                {camsDob && !validateAge18Plus(camsDob) && (
                  <p className="mb-2 text-xs text-red-400">You must be at least 18 years old.</p>
                )}
              </>
            )}
            <Button
              type="button"
              onClick={handleFetchFromCams}
              loading={fetchingCams}
              size="sm"
              disabled={camsDobRequired && (!camsDob || !validateAge18Plus(camsDob))}
            >
              Verify KYC
            </Button>
          </div>
        </div>

        {camsError && <div className="mb-3 text-sm text-red-400 bg-red-900/20 border border-red-500/30 px-4 py-2.5 rounded-xl">{camsError}</div>}
        {camsSuccess && <div className="mb-3 text-sm text-green-400 bg-green-900/20 border border-green-500/30 px-4 py-2.5 rounded-xl">{camsSuccess}</div>}

        <div className="rounded-xl border border-dark-600 bg-dark-900/60 p-4 text-sm text-gray-400">
          Click “Verify KYC” to check your existing record. After this step, you will be taken to eSign.
        </div>

        {kycStatus && kycStatus.status === 'rejected' && (
          <div className="mt-2 rounded-xl bg-red-900/20 border border-red-500/30 p-4 sm:p-6">
            <div className="flex items-center">
              <XCircleIcon className="w-6 h-6 text-red-500 mr-3" />
              <h3 className="text-lg font-medium text-red-300">KYC Rejected</h3>
            </div>
            {kycStatus.admin_remarks && (
              <p className="mt-2 text-sm text-red-300">
                <strong>Reason:</strong> {kycStatus.admin_remarks}
              </p>
            )}
            <p className="mt-2 text-sm text-red-300">
              Please review and resubmit your KYC with correct information.
            </p>
          </div>
        )}
      </div>
    </OnboardingLayout>
  )
}

export default KYCPage
